/**
 * Proactive Token Refresh Service
 *
 * Schedules a silent refresh at 75 % of the access-token lifetime so users
 * never hit a 401 during normal usage. The 401 interceptor in APIv1 acts as a
 * safety net for edge cases (tab sleep, network outage, etc.).
 *
 * This module is intentionally dependency-free of the Zustand store to avoid
 * circular imports (APIv1 ↔ user-store). Instead it uses injected callbacks.
 */

import axios from 'axios';
import _ from 'lodash';
import config from '../../config';

// ----- Axios instance for refresh calls (separate from API_V1) ---------------

const refreshAxios = axios.create({
  baseURL: _.get(config, 'apiBaseUrl'),
  responseType: 'json',
  withCredentials: true,
});

// ----- Injected callbacks (set once by user-store at boot) -------------------

type GetRefreshToken = () => string | undefined;
type SetTokenData = (data: any) => void;

let _getRefreshToken: GetRefreshToken = () => undefined;
let _setTokenData: SetTokenData = () => {};

export function initTokenRefresh(
  getRefreshToken: GetRefreshToken,
  setTokenData: SetTokenData,
): void {
  _getRefreshToken = getRefreshToken;
  _setTokenData = setTokenData;
}

// ----- Refresh call ----------------------------------------------------------

export const refreshTokenCall = async () => {
  try {
    const refreshToken = _getRefreshToken();
    if (!refreshToken) {
      return { error: 'No refresh token available', data: [] };
    }

    const refreshResponse = await refreshAxios.post('/auth/refresh', {
      refreshToken,
    });

    if (!_.isNil(refreshResponse.data)) {
      _setTokenData(refreshResponse.data);
    }

    return refreshResponse.data;
  } catch (e) {
    return { error: _.get(e, 'response.data', e), data: [] };
  }
};

// ----- Proactive timer -------------------------------------------------------

const DEFAULT_EXPIRES_IN_S = 3600;
const REFRESH_RATIO = 0.75;

let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

export function scheduleProactiveRefresh(expiresInSeconds?: number | string): void {
  clearProactiveRefresh();

  const ttl =
    typeof expiresInSeconds === 'number'
      ? expiresInSeconds
      : typeof expiresInSeconds === 'string'
        ? parseInt(expiresInSeconds, 10) || DEFAULT_EXPIRES_IN_S
        : DEFAULT_EXPIRES_IN_S;

  const delayMs = Math.max(ttl * REFRESH_RATIO * 1000, 30_000);

  proactiveRefreshTimer = setTimeout(async () => {
    const res = await refreshTokenCall();
    if (!res.error) {
      scheduleProactiveRefresh(res.expiresIn);
    }
  }, delayMs);
}

export function clearProactiveRefresh(): void {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
}
